import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import type {
  Agreement,
  AgreementType,
  CreateAgreementInput,
  AgreementStatus,
} from '@catalyst-psa/shared';
import { AGREEMENT_STATUSES, AGREEMENT_TYPES, AGREEMENT_TYPE_LABELS } from '@catalyst-psa/shared';

import { ApiError, mutationError } from '../../api/client.js';
import { useAuth } from '../../auth/authContext.js';
import { Button } from '../../components/ui/Button.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { CheckboxField, SelectField, TextAreaField, TextField } from '../../components/ui/Field.js';
import { Modal } from '../../components/ui/Modal.js';
import { FormActions, useSaveMode } from '../../components/ui/FormActions.js';
import { CustomerSelectField } from '../customers/CustomerSelectField.js';
import { useAgreementSubtypeOptions } from '../agreement-subtypes/api.js';
import {
  useCreateAgreement,
  useDeleteAgreement,
  useSeedAgreementLinesFromOpportunity,
  useUpdateAgreement,
} from './api.js';
import { AGREEMENT_STATUS_LABELS } from './status.js';

interface AgreementFormModalProps {
  isOpen: boolean;
  /** Existing agreement to edit, or null to create a new one. */
  agreement: Agreement | null;
  /** When creating, pre-selects this customer (e.g. launched from an onboarding record). */
  defaultCustomerId?: string;
  /** When creating, seed the agreement's lines from this opportunity's products. */
  seedFromOpportunityId?: string;
  /** Called after a successful create (e.g. to link the agreement to an onboarding record). */
  onCreated?: (agreement: Agreement) => void;
  onClose: () => void;
  /** Called after a successful delete (e.g. to navigate away from a detail page). */
  onDeleted?: () => void;
}

interface FormState {
  customerId: string;
  name: string;
  type: AgreementType;
  status: AgreementStatus;
  subtypeId: string;
  startDate: string;
  noEndDate: boolean;
  endDate: string;
  rateIncreaseDate: string;
  rateIncreasePercent: string;
  customerPo: string;
  internalNotes: string;
  hoursIncluded: string;
  allowRollover: boolean;
  hoursCap: string;
  overageRate: string;
  hideHoursOnInvoicePdf: boolean;
  hideZerosOnInvoiceLines: boolean;
}

const EMPTY_FORM: FormState = {
  customerId: '',
  name: '',
  type: 'product_subscription',
  status: 'draft',
  subtypeId: '',
  startDate: '',
  noEndDate: true,
  endDate: '',
  rateIncreaseDate: '',
  rateIncreasePercent: '',
  customerPo: '',
  internalNotes: '',
  hoursIncluded: '',
  allowRollover: false,
  hoursCap: '',
  overageRate: '',
  hideHoursOnInvoicePdf: false,
  hideZerosOnInvoiceLines: false,
};

function toFormState(agreement: Agreement | null, defaultCustomerId?: string): FormState {
  if (!agreement) {
    return { ...EMPTY_FORM, customerId: defaultCustomerId ?? '' };
  }
  return {
    customerId: agreement.customerId,
    name: agreement.name,
    type: agreement.type,
    status: agreement.status,
    subtypeId: agreement.subtypeId ?? '',
    startDate: agreement.startDate,
    noEndDate: agreement.endDate === null,
    endDate: agreement.endDate ?? '',
    rateIncreaseDate: agreement.rateIncreaseDate ?? '',
    rateIncreasePercent: agreement.rateIncreasePercent ?? '',
    customerPo: agreement.customerPo ?? '',
    internalNotes: agreement.internalNotes ?? '',
    hoursIncluded: agreement.hoursIncluded ?? '',
    allowRollover: agreement.allowRollover,
    hoursCap: agreement.hoursCap ?? '',
    overageRate: agreement.overageRate ?? '',
    hideHoursOnInvoicePdf: agreement.hideHoursOnInvoicePdf,
    hideZerosOnInvoiceLines: agreement.hideZerosOnInvoiceLines,
  };
}

function fieldErrorsOf(error: unknown): Record<string, string> {
  if (error instanceof ApiError && error.envelope?.error.details) {
    const result: Record<string, string> = {};
    for (const detail of error.envelope.error.details) {
      result[detail.path] = detail.message;
    }
    return result;
  }
  return {};
}

const STATUS_OPTIONS = AGREEMENT_STATUSES.map((status) => ({
  value: status,
  label: AGREEMENT_STATUS_LABELS[status],
}));

const TYPE_OPTIONS = AGREEMENT_TYPES.map((type) => ({
  value: type,
  label: AGREEMENT_TYPE_LABELS[type],
}));

const FORM_ID = 'agreement-form';

export function AgreementFormModal({
  isOpen,
  agreement: agreementProp,
  defaultCustomerId,
  seedFromOpportunityId,
  onCreated,
  onClose,
  onDeleted,
}: AgreementFormModalProps): JSX.Element {
  const navigate = useNavigate();
  const [agreement, setAgreement] = useState<Agreement | null>(agreementProp);
  const isEdit = agreement !== null;
  const { isAdmin } = useAuth();
  const saveMode = useSaveMode();
  const [form, setForm] = useState<FormState>(() => toFormState(agreement, defaultCustomerId));
  const [confirmOpen, setConfirmOpen] = useState(false);

  const createMutation = useCreateAgreement();
  const updateMutation = useUpdateAgreement();
  const deleteMutation = useDeleteAgreement();
  const seedMutation = useSeedAgreementLinesFromOpportunity();
  const mutation = isEdit ? updateMutation : createMutation;

  const resetCreate = createMutation.reset;
  const resetUpdate = updateMutation.reset;
  const resetDelete = deleteMutation.reset;
  const resetSaveMode = saveMode.reset;

  const subtypesQuery = useAgreementSubtypeOptions();
  const subtypeOptions = useMemo(
    () => (subtypesQuery.data ?? []).map((subtype) => ({ value: subtype.id, label: subtype.name })),
    [subtypesQuery.data],
  );

  useEffect(() => {
    if (isOpen) {
      setAgreement(agreementProp);
    }
  }, [isOpen, agreementProp]);

  useEffect(() => {
    if (isOpen) {
      setForm(toFormState(agreement, defaultCustomerId));
      setConfirmOpen(false);
      resetSaveMode();
      resetCreate();
      resetUpdate();
      resetDelete();
    }
  }, [isOpen, agreement, defaultCustomerId, resetSaveMode, resetCreate, resetUpdate, resetDelete]);

  const fieldErrors = fieldErrorsOf(mutation.error);
  const generalError =
    mutation.error instanceof ApiError && !mutation.error.envelope?.error.details?.length
      ? mutation.error.message
      : mutation.isError && !(mutation.error instanceof ApiError)
        ? 'Failed to save agreement.'
        : null;

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();
    const isManaged = form.type === 'managed_services';
    const managedFields = {
      hoursIncluded: isManaged && form.hoursIncluded.trim() ? form.hoursIncluded.trim() : null,
      allowRollover: isManaged ? form.allowRollover : false,
      hoursCap: isManaged && form.hoursCap.trim() ? form.hoursCap.trim() : null,
      overageRate: isManaged && form.overageRate.trim() ? form.overageRate.trim() : null,
      hideHoursOnInvoicePdf: isManaged ? form.hideHoursOnInvoicePdf : false,
    };
    const sharedFields = {
      endDate: form.noEndDate ? null : form.endDate ? form.endDate : null,
      rateIncreaseDate: form.rateIncreaseDate ? form.rateIncreaseDate : null,
      rateIncreasePercent: form.rateIncreasePercent.trim() ? form.rateIncreasePercent.trim() : null,
      customerPo: form.customerPo.trim() ? form.customerPo.trim() : null,
      internalNotes: form.internalNotes.trim() ? form.internalNotes.trim() : null,
      subtypeId: form.subtypeId ? form.subtypeId : null,
      hideZerosOnInvoiceLines: form.hideZerosOnInvoiceLines,
    };

    if (isEdit) {
      updateMutation.mutate(
        {
          id: agreement.id,
          input: {
            name: form.name.trim(),
            type: form.type,
            status: form.status,
            startDate: form.startDate,
            ...sharedFields,
            ...managedFields,
          },
        },
        { onSuccess: () => saveMode.closeAfterSaveRef.current && onClose() },
      );
    } else {
      const payload: CreateAgreementInput = {
        customerId: form.customerId,
        name: form.name.trim(),
        type: form.type,
        startDate: form.startDate,
        ...sharedFields,
        ...managedFields,
      };
      createMutation.mutate(payload, {
        onSuccess: async (created) => {
          if (seedFromOpportunityId) {
            try {
              await seedMutation.mutateAsync({
                agreementId: created.id,
                opportunityId: seedFromOpportunityId,
              });
            } catch {
              // Best-effort: the agreement is created even if line seeding fails.
            }
          }
          onCreated?.(created);
          if (saveMode.closeAfterSaveRef.current) {
            onClose();
          } else {
            setAgreement(created);
          }
        },
      });
    }
  };

  const handleDelete = (): void => {
    if (!agreement) {
      return;
    }
    deleteMutation.mutate(agreement.id, {
      onSuccess: () => {
        setConfirmOpen(false);
        onClose();
        onDeleted?.();
      },
    });
  };

  const goToCustomer = (): void => {
    const targetCustomerId = form.customerId || agreement?.customerId;
    if (!targetCustomerId) {
      return;
    }
    navigate(`/customers?returnCustomerId=${targetCustomerId}&returnTab=agreements`);
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={isEdit ? 'Edit agreement' : 'New agreement'}
        size="wide"
        headerActions={
          <Button
            variant="secondary"
            size="sm"
            onClick={goToCustomer}
            disabled={!form.customerId && !agreement?.customerId}
          >
            Go to Customer
          </Button>
        }
        footer={
          <FormActions
            formId={FORM_ID}
            saveMode={saveMode}
            isPending={mutation.isPending}
            onCancel={onClose}
            leading={
              isEdit && isAdmin ? (
                <Button
                  variant="ghost"
                  className="mr-auto text-rose-600 hover:bg-rose-50"
                  onClick={() => setConfirmOpen(true)}
                  disabled={mutation.isPending || deleteMutation.isPending}
                >
                  Delete
                </Button>
              ) : null
            }
          />
        }
      >
        <form id={FORM_ID} onSubmit={handleSubmit} className="flex flex-col gap-4">
          {generalError ? (
            <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{generalError}</p>
          ) : null}

          {isEdit ? null : (
            <CustomerSelectField
              label="Customer"
              required
              value={form.customerId}
              error={fieldErrors.customerId}
              onChange={(customerId) => setForm((prev) => ({ ...prev, customerId }))}
            />
          )}

          <TextField
            label="Name"
            required
            value={form.name}
            error={fieldErrors.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
          />

          <SelectField
            label="Type"
            value={form.type}
            options={TYPE_OPTIONS}
            error={fieldErrors.type}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, type: e.target.value as AgreementType }))
            }
          />

          <SelectField
            label="Subtype"
            value={form.subtypeId}
            placeholder="None"
            options={subtypeOptions}
            error={fieldErrors.subtypeId}
            onChange={(e) => setForm((prev) => ({ ...prev, subtypeId: e.target.value }))}
          />

          {form.type === 'product_subscription' || form.type === 'managed_services' ? (
            <div>
              <CheckboxField
                label="Hide zeros on invoice lines"
                checked={form.hideZerosOnInvoiceLines}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, hideZerosOnInvoiceLines: e.target.checked }))
                }
              />
              <p className="mt-1 text-xs text-slate-500">
                Lines with a 0 quantity or a $0 price are left off generated invoices.
              </p>
            </div>
          ) : null}

          {form.type === 'managed_services' ? (
            <div className="grid grid-cols-2 gap-4 rounded-lg border border-slate-200 p-3">
              <TextField
                label="Hours included"
                type="number"
                min={0}
                step="0.01"
                value={form.hoursIncluded}
                error={fieldErrors.hoursIncluded}
                onChange={(e) => setForm((prev) => ({ ...prev, hoursIncluded: e.target.value }))}
              />
              <TextField
                label="Overage rate"
                type="number"
                min={0}
                step="0.01"
                value={form.overageRate}
                error={fieldErrors.overageRate}
                onChange={(e) => setForm((prev) => ({ ...prev, overageRate: e.target.value }))}
              />
              <TextField
                label="Hours cap"
                type="number"
                min={0}
                step="0.01"
                value={form.hoursCap}
                error={fieldErrors.hoursCap}
                onChange={(e) => setForm((prev) => ({ ...prev, hoursCap: e.target.value }))}
              />
              <div className="flex items-end">
                <CheckboxField
                  label="Allow roll-over"
                  checked={form.allowRollover}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, allowRollover: e.target.checked }))
                  }
                />
              </div>
              <div className="col-span-2">
                <CheckboxField
                  label="Hide hours on PDF invoice"
                  checked={form.hideHoursOnInvoicePdf}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, hideHoursOnInvoicePdf: e.target.checked }))
                  }
                />
              </div>
            </div>
          ) : null}

          {isEdit ? (
            <SelectField
              label="Status"
              value={form.status}
              options={STATUS_OPTIONS}
              error={fieldErrors.status}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, status: e.target.value as AgreementStatus }))
              }
            />
          ) : null}

          <TextField
            label="Start date"
            type="date"
            required
            value={form.startDate}
            error={fieldErrors.startDate}
            onChange={(e) => setForm((prev) => ({ ...prev, startDate: e.target.value }))}
          />

          <CheckboxField
            label="No end date"
            checked={form.noEndDate}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                noEndDate: e.target.checked,
                endDate: e.target.checked ? '' : prev.endDate,
              }))
            }
          />

          {form.noEndDate ? null : (
            <TextField
              label="End date"
              type="date"
              value={form.endDate}
              error={fieldErrors.endDate}
              onChange={(e) => setForm((prev) => ({ ...prev, endDate: e.target.value }))}
            />
          )}

          <div className="grid grid-cols-2 gap-4">
            <TextField
              label="Rate increase date"
              type="date"
              value={form.rateIncreaseDate}
              error={fieldErrors.rateIncreaseDate}
              onChange={(e) => setForm((prev) => ({ ...prev, rateIncreaseDate: e.target.value }))}
            />
            <TextField
              label="Rate increase percent"
              type="number"
              min={0}
              step="0.01"
              value={form.rateIncreasePercent}
              error={fieldErrors.rateIncreasePercent}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, rateIncreasePercent: e.target.value }))
              }
            />
          </div>

          <TextField
            label="Customer PO"
            value={form.customerPo}
            maxLength={128}
            error={fieldErrors.customerPo}
            onChange={(e) => setForm((prev) => ({ ...prev, customerPo: e.target.value }))}
          />

          <TextAreaField
            label="Internal notes"
            rows={4}
            value={form.internalNotes}
            error={fieldErrors.internalNotes}
            onChange={(e) => setForm((prev) => ({ ...prev, internalNotes: e.target.value }))}
          />
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={confirmOpen}
        title="Delete agreement"
        message="This permanently deletes the agreement and its lines. Agreements with closed periods cannot be deleted — end-date them instead."
        error={mutationError(deleteMutation.error, 'Failed to delete the agreement.')}
        confirmLabel="Delete"
        destructive
        isConfirming={deleteMutation.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
}
